import {
  ArticleCard,
  BannerCard,
  Icon,
  Pagination,
  PostCard,
  ProductCard,
} from "components";
import { types } from "constant";
import {
  MagazineLoading,
  NoMagazine,
  SelectMagazineTypeDrawer,
} from "containers";
import SelectRoomDrawer from "containers/SelectRoomDrawer";
import { MagazineType } from "enums";
import { useAxios, useToggle } from "hooks";
import { isEqual } from "lodash";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Magazine, Paged, Room } from "types";
import { PersonDetailsContext } from ".";

type Params = {
  pageNumber: number;
  pageSize: number;
};

enum MagazineItemType {
  All,
  PostsAndArticles,
  Posts,
  Articles,
  Store,
}

const magazinePageSize = 20;

const getMagazineItemType = (type: MagazineType[] | null) => {
  if (!type?.length) return MagazineItemType.All;
  if (isEqual(type, [MagazineType.Post, MagazineType.Article])) {
    return MagazineItemType.PostsAndArticles;
  }
  if (isEqual(type, [MagazineType.Post])) return MagazineItemType.Posts;
  if (isEqual(type, [MagazineType.Article])) return MagazineItemType.Articles;
  return MagazineItemType.Store;
};

export default function Links() {
  const [axios, loading] = useAxios();
  const { user } = useContext(PersonDetailsContext);
  const [openSelectRoom, toggleSelectRoom] = useToggle(false);
  const [openSelectMagazineType, toggleSelectMagazineType] = useToggle(false);
  const [activeRoom, setActiveRoom] = useState<Room.Item | null>(null);
  const [activeType, setActiveType] = useState<MagazineType[] | null>(null);

  const [magazine, setMagazine] = useState<Paged<Magazine.Item> | null>(null);

  const activeTypeTitle = useMemo(() => {
    return types.magazine.find((e) => isEqual(e.id, activeType))?.name;
  }, [activeType]);

  const [params, setParams] = useState<Params>({
    pageSize: magazinePageSize,
    pageNumber: 1,
  });
  const hasItems = !!magazine?.items.length || !!loading;
  const cardItems = useMemo(() => {
    return magazine?.items.map((e) => {
      const key = `${e.itemType}-${e.magazineItem.id}`;
      if (e.itemType === Magazine.ItemType.Post) {
        return (
          <PostCard
            key={key}
            post={e.magazineItem as Magazine.PostItem}
          />
        );
      }
      if (e.itemType === Magazine.ItemType.Product) {
        return (
          <ProductCard
            key={key}
            product={e.magazineItem as Magazine.ProductItem}
          />
        );
      }
      if (e.itemType === Magazine.ItemType.Article) {
        return (
          <ArticleCard
            key={key}
            article={e.magazineItem as Magazine.ArticleItem}
          />
        );
      }
      return (
        <BannerCard
          key={key}
          banner={e.magazineItem as Magazine.BannerItem}
        />
      );
    });
  }, [magazine?.items]);
  const handleSetParams = (key: keyof Params) => {
    return (value: any) => {
      setParams((p) => ({ ...p, pageNumber: 1, [key]: value }));
    };
  };
  const handleSetActiveRoom = (room: Room.Item | null) => {
    setActiveRoom(room);
    setParams((p) => ({ ...p, pageNumber: 1 }));
  };
  const handleSetActiveType = (type: MagazineType[] | null) => {
    setActiveType(type);
    setParams((p) => ({ ...p, pageNumber: 1 }));
  };
  const getMagazine = () => {
    const url = `/People/${user.id}/get-all-public-magazine`;
    const requestParams = {
      ...params,
      magazineItemType: getMagazineItemType(activeType),
      ...(activeRoom?.id && { roomId: activeRoom.id }),
    };
    const isFirstPage = requestParams.pageNumber === 1;
    if (isFirstPage) setMagazine(null);
    axios.get<Paged<Magazine.Item>>(url, { params: requestParams }).then(({ data }) => {
      if (isFirstPage) return setMagazine(data);
      setMagazine((p) => ({ ...data, items: [...(p?.items ?? []), ...data.items] }));
    });
  };

  useEffect(getMagazine, [axios, params, user.id, activeRoom?.id, activeType]);
  return (
    <Fragment>
      <section className="flex items-center justify-between gap-4 mb-5 px-5">
        <h2
          className="text-base text-[#353740] font-medium cursor-pointer"
          onClick={toggleSelectRoom}
        >
          {activeRoom?.title ?? "All Rooms"}{" "}
          <Icon name="ArrowDown2" variant="Bold" className="size-4" />
        </h2>
        {/* <IconButton type="button">
          <Icon name="SearchNormal1" />
        </IconButton> */}
      </section>
      <section className="space-y-1 mb-4 px-5">
        <div className="w-full h-2.5 flex items-center">
          <hr className="border-[#ECEFF2] w-full" />
        </div>
        <h2
          className="text-base text-primary text-center font-semibold cursor-pointer"
          onClick={toggleSelectMagazineType}
        >
          {activeTypeTitle ?? "All Magazine"}{" "}
          <Icon name="ArrowDown2" variant="Bold" className="size-4" />
        </h2>
        <div className="w-full h-2.5 flex items-center">
          <hr className="border-[#ECEFF2] w-full" />
        </div>
      </section>
      <section className="space-y-4">
        {!hasItems && <NoMagazine inRoom={!!activeRoom} />}
        {/* <ArticleCard />
        <ProductCard />
        <PostCard /> */}
        {cardItems}
        {loading ? (
          <MagazineLoading />
        ) : (
          <Pagination
            activePage={params.pageNumber}
            setActivePage={handleSetParams("pageNumber")}
            totalPages={magazine?.totalPages}
          />
        )}
      </section>
      <SelectRoomDrawer
        isOpen={openSelectRoom}
        toggle={toggleSelectRoom}
        activeRoom={activeRoom}
        onSelect={handleSetActiveRoom}
      />
      <SelectMagazineTypeDrawer
        isOpen={openSelectMagazineType}
        toggle={toggleSelectMagazineType}
        activeType={activeType}
        onSelect={handleSetActiveType}
      />
    </Fragment>
  );
}
